/* oxlint-disable @typescript-eslint/no-explicit-any */
import {
  streamMatchers,
  type StreamMatchers,
} from "../utils/testing/stream.js";
import { BaseMessage } from "../messages/index.js";
import { HumanMessage } from "../messages/index.js";
import { AIMessage } from "../messages/index.js";
import { SystemMessage } from "../messages/index.js";
import { ToolMessage } from "../messages/index.js";

/**
 * The `this` context that Vitest and Jest provide to custom matchers
 * via `expect.extend`.
 *
 * Compatible with both frameworks:
 * - https://vitest.dev/guide/extending-matchers.html
 * - https://jestjs.io/docs/expect#expectextendmatchers
 */
interface ExpectExtendThis {
  isNot?: boolean;
  equals(a: unknown, b: unknown): boolean;
  utils: {
    matcherHint(name: string, received?: string, expected?: string): string;
    printReceived(value: unknown): string;
    printExpected(value: unknown): string;
  };
}

interface ExpectationResult {
  pass: boolean;
  message: () => string;
  actual?: unknown;
  expected?: unknown;
}

function getMessageTypeName(msg: unknown): string {
  if (!BaseMessage.isInstance(msg)) return typeof msg;
  return msg.constructor.name || msg.type;
}

function makeMessageTypeMatcher(
  typeName: string,
  isInstance: (obj: unknown) => boolean
) {
  return function (
    this: ExpectExtendThis,
    received: unknown,
    expected?: string | Record<string, unknown>
  ): ExpectationResult {
    const { isNot, utils } = this;

    const instancePass = isInstance(received);
    if (!instancePass) {
      return {
        pass: false,
        message: () =>
          `${utils.matcherHint(`toBe${typeName}`, undefined, undefined)}\n\n` +
          `Expected: ${isNot ? "not " : ""}${typeName}\n` +
          `Received: ${getMessageTypeName(received)}`,
        actual: getMessageTypeName(received),
        expected: typeName,
      };
    }

    if (expected === undefined) {
      return {
        pass: true,
        message: () =>
          `${utils.matcherHint(`toBe${typeName}`, undefined, undefined)}\n\n` +
          `Expected: not ${typeName}\n` +
          `Received: ${typeName}`,
      };
    }

    const msg = received as BaseMessage;
    if (typeof expected === "string") {
      const contentPass = msg.content === expected;
      return {
        pass: contentPass,
        message: () =>
          `${utils.matcherHint(`toBe${typeName}`, undefined, undefined)}\n\n` +
          `Expected: ${typeName} with content ${utils.printExpected(expected)}\n` +
          `Received: ${typeName} with content ${utils.printReceived(msg.content)}`,
        actual: msg.content,
        expected,
      };
    }

    const fieldsPass = Object.entries(expected).every(([key, value]) =>
      this.equals((msg as any)[key], value)
    );
    return {
      pass: fieldsPass,
      message: () => {
        const receivedFields: Record<string, unknown> = {};
        for (const key of Object.keys(expected)) {
          receivedFields[key] = (msg as any)[key];
        }
        return (
          `${utils.matcherHint(`toBe${typeName}`, undefined, undefined)}\n\n` +
          `Expected: ${typeName} matching ${utils.printExpected(expected)}\n` +
          `Received: ${typeName} with ${utils.printReceived(receivedFields)}`
        );
      },
      actual: (() => {
        const receivedFields: Record<string, unknown> = {};
        for (const key of Object.keys(expected)) {
          receivedFields[key] = (msg as any)[key];
        }
        return receivedFields;
      })(),
      expected,
    };
  };
}

export const toBeHumanMessage = makeMessageTypeMatcher(
  "HumanMessage",
  HumanMessage.isInstance
);

export const toBeAIMessage = makeMessageTypeMatcher(
  "AIMessage",
  AIMessage.isInstance
);

export const toBeSystemMessage = makeMessageTypeMatcher(
  "SystemMessage",
  SystemMessage.isInstance
);

export const toBeToolMessage = makeMessageTypeMatcher(
  "ToolMessage",
  ToolMessage.isInstance
);

export function toHaveToolCalls(
  this: ExpectExtendThis,
  received: unknown,
  expected: Array<Record<string, unknown>>
): ExpectationResult {
  const { isNot, utils } = this;

  if (!AIMessage.isInstance(received)) {
    return {
      pass: false,
      message: () =>
        `${utils.matcherHint("toHaveToolCalls")}\n\n` +
        `Expected: AIMessage\n` +
        `Received: ${getMessageTypeName(received)}`,
    };
  }

  const actual = received.tool_calls ?? [];

  if (actual.length !== expected.length) {
    return {
      pass: false,
      message: () =>
        `${utils.matcherHint("toHaveToolCalls")}\n\n` +
        `Expected ${isNot ? "not " : ""}${expected.length} tool call(s), received ${actual.length}`,
      actual: actual.length,
      expected: expected.length,
    };
  }

  const unmatched = expected.filter(
    (exp) =>
      !actual.some((tc) =>
        Object.entries(exp).every(([key, value]) =>
          this.equals((tc as any)[key], value)
        )
      )
  );

  if (unmatched.length > 0) {
    return {
      pass: false,
      message: () =>
        `${utils.matcherHint("toHaveToolCalls")}\n\n` +
        `Could not find matching tool call(s) for:\n` +
        `${utils.printExpected(unmatched)}\n` +
        `Received tool calls: ${utils.printReceived(actual.map((tc) => ({ name: tc.name, id: tc.id, args: tc.args })))}`,
      actual: actual.map((tc) => ({ name: tc.name, id: tc.id, args: tc.args })),
      expected,
    };
  }

  return {
    pass: true,
    message: () =>
      `${utils.matcherHint("toHaveToolCalls")}\n\n` +
      `Expected AIMessage not to have matching tool calls`,
  };
}

export function toHaveToolCallCount(
  this: ExpectExtendThis,
  received: unknown,
  expected: number
): ExpectationResult {
  const { isNot, utils } = this;

  if (!AIMessage.isInstance(received)) {
    return {
      pass: false,
      message: () =>
        `${utils.matcherHint("toHaveToolCallCount")}\n\n` +
        `Expected: AIMessage\n` +
        `Received: ${getMessageTypeName(received)}`,
    };
  }

  const actual = received.tool_calls?.length ?? 0;
  const pass = actual === expected;

  return {
    pass,
    message: () =>
      `${utils.matcherHint("toHaveToolCallCount")}\n\n` +
      `Expected ${isNot ? "not " : ""}${expected} tool call(s)\n` +
      `Received: ${actual}`,
    actual,
    expected,
  };
}

export function toContainToolCall(
  this: ExpectExtendThis,
  received: unknown,
  expected: Record<string, unknown>
): ExpectationResult {
  const { isNot, utils } = this;

  if (!AIMessage.isInstance(received)) {
    return {
      pass: false,
      message: () =>
        `${utils.matcherHint("toContainToolCall")}\n\n` +
        `Expected: AIMessage\n` +
        `Received: ${getMessageTypeName(received)}`,
    };
  }

  const actual = received.tool_calls ?? [];
  const found = actual.some((tc) =>
    Object.entries(expected).every(([key, value]) =>
      this.equals((tc as any)[key], value)
    )
  );

  return {
    pass: found,
    message: () =>
      `${utils.matcherHint("toContainToolCall")}\n\n` +
      `Expected AIMessage ${isNot ? "not " : ""}to contain a tool call matching ${utils.printExpected(expected)}\n` +
      `Received tool calls: ${utils.printReceived(actual.map((tc) => ({ name: tc.name, id: tc.id })))}`,
    actual: actual.map((tc) => ({ name: tc.name, id: tc.id })),
    expected,
  };
}

export function toHaveToolMessages(
  this: ExpectExtendThis,
  received: unknown,
  expected: Array<Record<string, unknown>>
): ExpectationResult {
  const { isNot, utils } = this;

  if (!Array.isArray(received)) {
    return {
      pass: false,
      message: () =>
        `${utils.matcherHint("toHaveToolMessages")}\n\n` +
        `Expected an array of messages\n` +
        `Received: ${typeof received}`,
    };
  }

  const toolMessages = (received as BaseMessage[]).filter(
    ToolMessage.isInstance
  );

  if (toolMessages.length !== expected.length) {
    return {
      pass: false,
      message: () =>
        `${utils.matcherHint("toHaveToolMessages")}\n\n` +
        `Expected ${isNot ? "not " : ""}${expected.length} tool message(s), found ${toolMessages.length}`,
      actual: toolMessages.length,
      expected: expected.length,
    };
  }

  for (let i = 0; i < expected.length; i++) {
    const match = Object.entries(expected[i]).every(([key, value]) =>
      this.equals((toolMessages[i] as any)[key], value)
    );
    if (!match) {
      return {
        pass: false,
        message: () => {
          const receivedFields: Record<string, unknown> = {};
          for (const key of Object.keys(expected[i])) {
            receivedFields[key] = (toolMessages[i] as any)[key];
          }
          return (
            `${utils.matcherHint("toHaveToolMessages")}\n\n` +
            `Tool message at index ${i} did not match:\n` +
            `Expected: ${utils.printExpected(expected[i])}\n` +
            `Received: ${utils.printReceived(receivedFields)}`
          );
        },
        actual: toolMessages[i],
        expected: expected[i],
      };
    }
  }

  return {
    pass: true,
    message: () =>
      `${utils.matcherHint("toHaveToolMessages")}\n\n` +
      `Expected messages not to contain matching tool messages`,
  };
}

export function toHaveBeenInterrupted(
  this: ExpectExtendThis,
  received: unknown,
  expectedValue?: unknown
): ExpectationResult {
  const { isNot, utils } = this;

  const result = received as Record<string, any>;
  const interrupts = result?.__interrupt__;
  const hasInterrupt = Array.isArray(interrupts) && interrupts.length > 0;

  if (!hasInterrupt) {
    return {
      pass: false,
      message: () =>
        `${utils.matcherHint("toHaveBeenInterrupted")}\n\n` +
        `Expected result ${isNot ? "not " : ""}to have been interrupted\n` +
        `Received __interrupt__: ${utils.printReceived(interrupts)}`,
    };
  }

  if (expectedValue === undefined) {
    return {
      pass: true,
      message: () =>
        `${utils.matcherHint("toHaveBeenInterrupted")}\n\n` +
        `Expected result not to have been interrupted\n` +
        `Received ${interrupts.length} interrupt(s)`,
    };
  }

  const actualValue = interrupts[0]?.value;
  const valuePass = this.equals(actualValue, expectedValue);

  return {
    pass: valuePass,
    message: () =>
      `${utils.matcherHint("toHaveBeenInterrupted")}\n\n` +
      `Expected interrupt value: ${utils.printExpected(expectedValue)}\n` +
      `Received interrupt value: ${utils.printReceived(actualValue)}`,
    actual: actualValue,
    expected: expectedValue,
  };
}

export function toHaveStructuredResponse(
  this: ExpectExtendThis,
  received: unknown,
  expected?: Record<string, unknown>
): ExpectationResult {
  const { isNot, utils } = this;

  const result = received as Record<string, any>;
  const structuredResponse = result?.structuredResponse;
  const isDefined = structuredResponse !== undefined;

  if (!isDefined) {
    return {
      pass: false,
      message: () =>
        `${utils.matcherHint("toHaveStructuredResponse")}\n\n` +
        `Expected result ${isNot ? "not " : ""}to have a structured response\n` +
        `Received structuredResponse: undefined`,
    };
  }

  if (expected === undefined) {
    return {
      pass: true,
      message: () =>
        `${utils.matcherHint("toHaveStructuredResponse")}\n\n` +
        `Expected result not to have a structured response`,
    };
  }

  const fieldsPass = Object.entries(expected).every(([key, value]) =>
    this.equals(structuredResponse[key], value)
  );

  return {
    pass: fieldsPass,
    message: () =>
      `${utils.matcherHint("toHaveStructuredResponse")}\n\n` +
      `Expected structured response: ${utils.printExpected(expected)}\n` +
      `Received structured response: ${utils.printReceived(structuredResponse)}`,
    actual: structuredResponse,
    expected,
  };
}

/**
 * All matcher functions bundled for convenient use with `expect.extend()`.
 */
export const langchainMatchers = {
  toBeHumanMessage,
  toBeAIMessage,
  toBeSystemMessage,
  toBeToolMessage,
  toHaveToolCalls,
  toHaveToolCallCount,
  toContainToolCall,
  toHaveToolMessages,
  toHaveBeenInterrupted,
  toHaveStructuredResponse,
  ...streamMatchers,
};

export interface LangChainMatchers<R = unknown> extends StreamMatchers<R> {
  toBeHumanMessage(expected?: string | { content?: string; id?: string }): R;
  toBeAIMessage(expected?: string | { content?: string; name?: string }): R;
  toBeSystemMessage(
    expected?: string | { content?: string; additional_kwargs?: object }
  ): R;
  toBeToolMessage(
    expected?:
      | string
      | {
          content?: string;
          name?: string;
          status?: string;
          tool_call_id?: string;
        }
  ): R;
  toHaveToolCalls(
    expected: Array<{
      name?: string;
      id?: string;
      args?: Record<string, unknown>;
    }>
  ): R;
  toHaveToolCallCount(expected: number): R;
  toContainToolCall(expected: {
    name?: string;
    id?: string;
    args?: Record<string, unknown>;
  }): R;
  toHaveToolMessages(
    expected: Array<{
      content?: string;
      name?: string;
      status?: string;
      tool_call_id?: string;
    }>
  ): R;
  toHaveBeenInterrupted(expectedValue?: unknown): R;
  toHaveStructuredResponse(expected?: Record<string, unknown>): R;
}
